export const makeAcceptString = (extensions: string[]) =>
  extensions.map((ext) => `.${ext}`).join(",");
export const isValidExtension = (file: File, extensions: string[]) => {
  const extension = file.type.split("/").pop()?.split("+")[0];
  return !!extension && extensions.includes(extension);
};

/**
 * Image types supported by gmail from https://www.lifewire.com/attachment-types-supported-for-viewing-as-html-in-gmail-1172046
 * and https://blog.chromium.org/2011/05/webp-in-chrome-picasa-gmail-with-slew.html#:~:text=WebP%20is%20now%20natively%20supported,to%20reduce%20their%20storage%20needs
 */

// Matches image_file_extensions in core/constants.py
export const IMAGE_INPUT_EXTENSIONS = [
  "png",
  "jpg",
  "jpeg",
  "gif",
  "jfif",
  "webp",
  "svg",
  "bmp",
  // jp2, tif, tiff are images, but not supported in the browser so we'll call them "files"
];
export const IMAGE_INPUT_ACCEPT = makeAcceptString(IMAGE_INPUT_EXTENSIONS);
export const IMAGE_DROPZONE_ACCEPT = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg", ".jfif"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
  "image/svg+xml": [".svg"],
  "image/bmp": [".bmp"],
};
export const isValidImage = (file: File) => isValidExtension(file, IMAGE_INPUT_EXTENSIONS);

export const VIDEO_INPUT_EXTENSIONS = ["mp4", "quicktime", "mov", "webm"];
export const VIDEO_INPUT_ACCEPT = makeAcceptString(VIDEO_INPUT_EXTENSIONS);
export const isValidVideo = (file: File) => isValidExtension(file, VIDEO_INPUT_EXTENSIONS);

export const AUDIO_INPUT_EXTENSIONS = ["mp3", "mpeg", "wav", "m4a", "x-m4a"];
export const AUDIO_INPUT_ACCEPT = makeAcceptString(AUDIO_INPUT_EXTENSIONS);
export const isValidAudio = (file: File) => isValidExtension(file, AUDIO_INPUT_EXTENSIONS);

export const PDF_INPUT_EXTENSIONS = ["pdf"];
export const PDF_INPUT_ACCEPT = makeAcceptString(PDF_INPUT_EXTENSIONS);
export const isValidPdf = (file: File) => isValidExtension(file, PDF_INPUT_EXTENSIONS);

export const FILE_INPUT_EXTENSIONS = [
  ...IMAGE_INPUT_EXTENSIONS,
  ...PDF_INPUT_EXTENSIONS,
  ...VIDEO_INPUT_EXTENSIONS,
  ...AUDIO_INPUT_EXTENSIONS,
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "csv",
  "svg",
  "jp2",
  "tif",
  "tiff",
];
export const FILE_INPUT_ACCEPT = makeAcceptString(FILE_INPUT_EXTENSIONS);
export const isValidFile = (file: File) => isValidExtension(file, FILE_INPUT_EXTENSIONS);

export const TWILIO_IMAGE_INPUT_EXTENSIONS = ["jpeg", "jpg", "png", "gif"];
export const TWILIO_IMAGE_INPUT_ACCEPT = makeAcceptString(IMAGE_INPUT_EXTENSIONS);
export const isValidTwilioImage = (file: File) =>
  isValidExtension(file, TWILIO_IMAGE_INPUT_EXTENSIONS);

/**
 * Converts a base64 data URL to a File object
 * @param dataUrl - The base64 data URL (e.g., "data:image/png;base64,...")
 * @param filename - The filename for the resulting File
 * @returns A File object
 */
export const base64ToFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const bstr = atob(arr[1]);
  const u8arr = new Uint8Array(bstr.length);
  for (let i = 0; i < bstr.length; i += 1) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  return new File([u8arr], filename, { type: mime });
};
